import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { createAction, Store } from '@ngrx/store';
import { forkJoin, of } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap } from 'rxjs/operators';

import { PolicySummary } from '@amfam/policy/models/lib/models/policy-summary';
import { group642PartnerIds } from '@amfam/shared/models/lib/footer-partner/footer-partner-model';
import { EditMdmId } from '@amfam/shared/user/lib/+state/user.actions';
import { BrandService } from '@amfam/shared/utility/brand/lib/services/brand.service';

import { PolicyDocumentService } from '../../services/policy-documents.service';
import { PolicyService } from '../../services/policy.service';
import { DocumentsActionTypes } from '../documents/documents.actions';
import {
  LoadSummaries,
  SummariesAction,
  SummariesActionTypes,
  SummariesBillingAccountNumbersLoaded,
  SummariesLoadError,
  SummariesLoadSuccess,
  SummariesStatusNotFoundAction,
  UpdatePoiLink
} from './summaries.actions';
import { selectAllPolicySummaries } from './summaries.selectors';

// Helper function
function updateInsuranceLink(policy: PolicySummary, policyDocumentService: PolicyDocumentService) {
  if (policy.policyTypeDisplayName?.toUpperCase() === 'HOME')
    return policyDocumentService.getEOILinks(policy);
  else if (policy.policyTypeDisplayName?.toUpperCase() === 'AUTO')
    return policyDocumentService.getPOILinks(policy);
  return of(policy);
}
export const loadSummaries = createEffect(
  (actions$ = inject(Actions), policyService = inject(PolicyService)) => {
    return actions$.pipe(
      ofType(LoadSummaries),
      map(action => action.payload),
      switchMap(() =>
        policyService.getPolicySummaries().pipe(
          mergeMap(
            ({
              policies,
              afiMidvaleBillingAccountNumbers,
              phoenixHorisonBillingAccountNumbers,
              customerGroupId,
              customerSegmentCd,
              displayOptions,
              ga4Analytics,
              migrationBanner,
              policyAccessBanners
            }) => {
              // Include all policies that are either supported OR commercial
              const allPolicies = policies.filter(
                policy =>
                  policy.isCommercial ||
                  policyService.isPolicyTypeSupported(policy.generalizedProductType)
              );

              return [
                SummariesBillingAccountNumbersLoaded({
                  afiMidvaleBillingAccountNumbers,
                  phoenixHorisonBillingAccountNumbers,
                  customerGroupId,
                  customerSegmentCd,
                  displayOptions,
                  ga4Analytics,
                  migrationBanner,
                  policyAccessBanners
                }),
                SummariesLoadSuccess(allPolicies)
              ];
            }
          ),
          catchError(error => {
            const returnErrArray: SummariesAction[] = [];
            // action perform for 404 status code when there are no policies.
            if (error?.status?.code === 404) {
              returnErrArray.push(SummariesStatusNotFoundAction(error));
            } else {
              returnErrArray.push(SummariesLoadError(error));
            }

            return returnErrArray;
          })
        )
      )
    );
  },
  { functional: true }
);

export const updateMdmId = createEffect(
  (actions$ = inject(Actions)) => {
    return actions$.pipe(
      ofType(SummariesActionTypes.SummariesLoadSuccessType),
      map((action: SummariesAction) => action.payload),
      filter((policies: PolicySummary[]) => !!policies?.length && !!policies[0]?.customerMdmId),
      map((policies: PolicySummary[]) => EditMdmId(policies[0]?.customerMdmId as string)),
      catchError((err, source) => {
        console.error('[updateMdmId effect]', err);
        return source;
      })
    );
  },
  { functional: true }
);

export const loadBrandbyPartnerId = createEffect(
  (actions$ = inject(Actions), brandService = inject(BrandService)) => {
    return actions$.pipe(
      ofType(SummariesActionTypes.SummariesLoadSuccessType),
      map((action: SummariesAction) => {
        return action.payload;
      }),
      filter(policySummaries => !!(policySummaries?.length ?? 0)),
      mergeMap((payload: PolicySummary[]) => {
        const ids = payload.map(value => value.policyPartnerId);

        //TODO: Revisit this logic when adding homesite partners
        //For now, if all partner ids are part of group 642, we will load brand data
        //for each level 1 partner id to get specific phone numbers.
        const allLevel1Ids = ids.every(val => group642PartnerIds.includes(val));
        const isAfi = payload.filter(policy => policy.isAfi).length > 0;
        const allAfi = payload.filter(policy => policy.isAfi).length === ids.length;
        if (allLevel1Ids)
          return brandService.loadBrandForPolicy(Array.from(new Set(ids)), isAfi, allAfi);
        else return of(createAction('[Empty] Empty Action')());
      })
    );
  },
  { functional: true, dispatch: false }
);

export const updateOpertorPoiLinks = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    policyDocumentService = inject(PolicyDocumentService)
  ) => {
    return actions$.pipe(
      ofType(DocumentsActionTypes.LoadDocumentsCompleteType),
      concatLatestFrom(() => store.select(selectAllPolicySummaries)),
      switchMap(([, policies]) =>
        forkJoin(policies.map(policy => updateInsuranceLink(policy, policyDocumentService)))
      ),
      map(policies =>
        policies?.length > 0 ? UpdatePoiLink(policies) : createAction('[Empty] Empty Action')()
      )
    );
  },
  { functional: true }
);
